import React from "react";
import Tile from "@src/components/containers/Tile";
import { useToast } from "@swordsweeper/ssi-ui-component-library";
import PriceDisplay from "@src/components/common/PriceDisplay/PriceDisplay";
import { Link } from "react-router-dom";
import OrderSummaryTileViews, {
  OrderSummaryTileView,
} from "./OrderSummaryTileViews";
import * as OrderApi from "@src/api/OrderApi";
import { useNavigate } from "react-router-dom";
import DistributorLogo from "@src/components/common/DistributorLogo";
import styles from "./OrderSummaryTile.module.scss";

interface OrderSummaryTileProps {
  cart: any;
  view: OrderSummaryTileView;
  disabled?: boolean;
}

export default function OrderSummaryTile({
  cart,
  view,
  disabled,
}: OrderSummaryTileProps) {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const groupings = cart?.distributorProductGroupings ?? [];
  const hasAnyBackordered = groupings.some(
    (g: any) => g.products?.backordered?.length > 0,
  );
  const isMultiDistributor = groupings.length > 1;

  return (
    <Tile data-testid="order-summary">
      <h3>Order Summary</h3>

      {/* Global cart total + item count */}
      {view === OrderSummaryTileViews.Cart && (
        <div className="border-bottom padding-bottom-sm">
          <div className="font-weight-bold margin-top-md">
            <span className="text-md">
              <PriceDisplay
                amount={
                  cart.pricingBreakdown?.total
                    ? parseFloat(cart.pricingBreakdown.total)
                    : cart.subtotal
                }
                data-testid="cart-order-summary-total-price"
              />
            </span>
            <span className="text-xs margin-left-md">Total</span>
          </div>
          <div className="text-sm">
            {cart.itemCount} item{cart.itemCount === 1 ? "" : "s"}
          </div>
        </div>
      )}
      {view === OrderSummaryTileViews.Checkout && (
        <div className="padding-bottom-sm">
          <div className="font-weight-bold margin-top-md">
            <span className="text-xl">
              <PriceDisplay
                data-testid="checkout-order-summary-total-price"
                amount={cart.subtotal}
              />{" "}
              Total
            </span>
          </div>
          <span className="text-sm">
            <span>
              {cart.itemCount} item{cart.itemCount === 1 ? "" : "s"}
            </span>
            &nbsp;
            <span className="text-lightest text-xs">|</span>
            &nbsp;
            {/* TODO: What does "apply rewards" do? */}
            <a>Apply Rewards</a>
          </span>
        </div>
      )}

      {/* TODO: Make this the accordion content */}
      {/* Pricing breakdown by distributor */}
      {view === OrderSummaryTileViews.Cart &&
        cart.distributorProductGroupings.map(
          (distributorProductGrouping: any) => {
            const pricingBreakdown =
              cart.pricingBreakdown?.distributorBreakdowns?.[
                distributorProductGrouping.distributorSlug
              ];

            return (
              <div
                key={`distributor-product-grouping-summary-${distributorProductGrouping.distributorId}`}
                data-testid={`distributor-product-grouping-summary-${distributorProductGrouping.distributorId}`}
                className="padding-sm border-bottom"
              >
                <DistributorLogo
                  distributorSlug={distributorProductGrouping.distributorSlug}
                />
                <div className="spaced-content-v-xs">
                  {/* Always show subtotal */}
                  <div className="flex-row space-between font-weight-bold">
                    <span>Subtotal</span>
                    <span>
                      <PriceDisplay
                        data-testid="cart-order-summary-subtotal-price"
                        amount={parseFloat(
                          pricingBreakdown?.subtotal ||
                            distributorProductGrouping.subtotal.toString(),
                        )}
                      />
                    </span>
                  </div>

                  {/* Always show shipping */}
                  <div className="flex-row space-between text-sm">
                    <span className="font-weight-bold">Shipping</span>
                    <span>
                      <PriceDisplay
                        data-testid="cart-order-summary-shipping-price"
                        amount={parseFloat(pricingBreakdown?.shipping || "0")}
                      />
                    </span>
                  </div>

                  {/* Always show tax */}
                  <div className="flex-row space-between text-sm">
                    <span className="font-weight-bold">Tax</span>
                    <span>Calculated at checkout</span>
                  </div>

                  {/* Show fees if available */}
                  {pricingBreakdown?.fees &&
                    pricingBreakdown.fees.length > 0 &&
                    pricingBreakdown.fees.map((fee, index) => (
                      <div
                        key={index}
                        className="flex-row space-between text-sm"
                      >
                        <span className="font-weight-bold">
                          {fee.description}
                        </span>
                        <span>
                          <PriceDisplay
                            data-testid={`cart-order-summary-fee-${index}`}
                            amount={fee.amount}
                          />
                        </span>
                      </div>
                    ))}

                  {/* Show note if available */}
                  {pricingBreakdown?.note && (
                    <div className="text-xs text-lightest">
                      {pricingBreakdown.note}
                    </div>
                  )}

                  {isMultiDistributor &&
                    distributorProductGrouping.products?.backordered?.length >
                      0 && (
                      <div
                        className={styles.miniNotice}
                        data-testid={`order-summary-backorder-notice-${distributorProductGrouping.distributorSlug}`}
                      >
                        <i
                          className={`fa-light fa-truck-clock ${styles.miniNoticeIcon}`}
                          aria-hidden="true"
                        />
                        <span>Order contains backordered item(s).</span>
                      </div>
                    )}

                  {/* No per-distributor total row — only the global total at the top */}
                </div>
              </div>
            );
          },
        )}

      {view === OrderSummaryTileViews.Cart && hasAnyBackordered && (
          <div
            className={styles.callout}
            data-testid="order-summary-backorder-callout"
          >
            <i className={`fa-light fa-truck-clock ${styles.calloutIcon}`} aria-hidden="true" />
            <div>
              <div className={styles.calloutHeading}>
                Your order has backordered items.
              </div>
              <div className={styles.calloutBody}>
                You&apos;ll be contacted when they are back in stock. Then{" "}
                <Link to="/support" className={styles.calloutLink}>
                  contact your rep
                </Link>{" "}
                to purchase your backordered items.
              </div>
            </div>
          </div>
        )}
    </Tile>
  );
}
